import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import { useSelector } from "react-redux";
import { useStatus } from "../../popup/NotificationContext";
import {
  useAddLicenseMutation,
  useGetLicenseRecipientsQuery,
  useGetLicensesQuery,
  useUpdateLicenseMutation,
} from "../../redux/logistics/logisticsApi";
import { hasPermission, normalizeRole, ROLES } from "../../utils/rbacHelper";
import FlexibleStatsRow from "../common/FlexibleStatsRow";

const LICENSE_TYPES = ["Daily", "Monthly", "Yearly", "Lifetime", "Fuel", "Dashcam"];
const LICENSE_STATUSES = ["Completed", "Pending"];
const TRANSACTION_TYPES = ["Sell", "Reverse"];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
    minHeight: 54,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
  },
};

const purchaseSectionCardSx = {
  p: { xs: 1.5, md: 1.75 },
  borderRadius: 3,
  border: "1px solid rgba(226,232,240,0.9)",
  background:
    "linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,0.98) 100%)",
};

const purchaseSectionTitleSx = {
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#64748b",
  mb: 1.4,
};

const panelSx = {
  borderRadius: 4,
  borderColor: "rgba(226,232,240,0.95)",
  backgroundColor: "rgba(255,255,255,0.96)",
  boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
};

const formatDateCell = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const dateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const buildCsv = (rows) => {
  const headers = ["From", "To", "Currency", "Amount", "Units", "Date", "Status", "Payment Amount", "Payment Date", "Type", "Document", "Remarks", "Operation"];
  const body = rows.map((row) =>
    [
      row.fromName,
      row.toName,
      row.currency,
      row.unitAmount,
      row.count,
      formatDateCell(row.createdAt),
      row.status,
      row.paymentAmount,
      formatDateCell(row.paymentDate),
      row.type,
      row.documentPath,
      row.remarks,
      row.transactionType,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const createDefaultFormData = () => ({
  dealer: "",
  unitAmount: 100,
  currency: "INR",
  count: 0,
  tax: 18,
  total: 0,
  status: "Completed",
  paymentAmount: 0,
  paymentDate: dateInputValue(new Date()),
  type: "Yearly",
  remarks: "",
  transactionType: "Sell",
  file: null,
});

const statusTone = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("expired")) {
    return { bg: "#fee2e2", color: "#dc2626", label: "Expired" };
  }
  if (value.includes("expiring")) {
    return { bg: "#ffedd5", color: "#ea580c", label: "Expiring Soon" };
  }
  if (value === "active") {
    return { bg: "#dcfce7", color: "#16a34a", label: "Active" };
  }
  if (value.includes("completed")) {
    return { bg: "#dcfce7", color: "#16a34a", label: "Completed" };
  }
  if (value.includes("pending")) {
    return { bg: "#e0e7ff", color: "#4f46e5", label: "Pending" };
  }
  return { bg: "#e2e8f0", color: "#475569", label: status || "Unknown" };
};

// License expiry is governed by the date selected when the license is created.
// Keep this presentation calculation in the client; it does not mutate status
// or change any backend license behavior.
const getLicenseLifecycle = (license, now = Date.now()) => {
  const explicitStatus = String(license?.status || "").toLowerCase();
  if (explicitStatus.includes("expired")) return "expired";

  const validTo = new Date(license?.validTo || license?.expiryDate || "");
  if (!Number.isNaN(validTo.getTime())) {
    const daysRemaining = Math.ceil((validTo.getTime() - now) / (24 * 60 * 60 * 1000));
    if (daysRemaining < 0) return "expired";
    if (daysRemaining <= 30) return "expiring";
  }

  return "active";
};

const LicensePortalModal = ({ open = true, onClose, asPage = false, initialSection = "licenses" }) => {
  const { showStatus } = useStatus();
  const currentUser = useSelector((state) => state.auth?.user || null);
  const { data: licensesPayload = [], refetch } = useGetLicensesQuery();
  const { data: recipients = [] } = useGetLicenseRecipientsQuery();
  const [addLicense, { isLoading: isSaving }] = useAddLicenseMutation();
  const [updateLicense, { isLoading: isUpdatingLicense }] = useUpdateLicenseMutation();

  const tableRef = useRef(null);
  const formRef = useRef(null);
  const summaryRef = useRef(null);

  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedOperation, setSelectedOperation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState("table");
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [transactionToggle, setTransactionToggle] = useState("Sell");
  const [formData, setFormData] = useState(createDefaultFormData);

  const currentUserRole = normalizeRole(currentUser?.role);
  const canPurchaseLicense = hasPermission(currentUserRole, "purchaseLicense");
  const currentUserIdentity = useMemo(
    () => ({
      id: String(currentUser?._id || currentUser?.id || ""),
      username: String(currentUser?.username || "").trim().toLowerCase(),
      email: String(currentUser?.email || "").trim().toLowerCase(),
      uniqueId: String(currentUser?.uniqueId || "").trim().toLowerCase(),
    }),
    [currentUser]
  );
  const normalizedRecipients = useMemo(() => {
    if (!Array.isArray(recipients)) return [];
    const allowedRoles =
      currentUserRole === ROLES.SUPER_ADMIN
        ? new Set([ROLES.SUB_ADMIN, ROLES.DEALER])
        : new Set([ROLES.DEALER]);
    return recipients
      .filter((item) => allowedRoles.has(normalizeRole(item?.role)))
      .sort((left, right) => {
        const leftRole = normalizeRole(left?.role);
        const rightRole = normalizeRole(right?.role);
        if (leftRole !== rightRole) return leftRole === ROLES.SUB_ADMIN ? -1 : 1;
        return String(left?.username || "").localeCompare(String(right?.username || ""));
      });
  }, [currentUserRole, recipients]);
  const normalizedLicenses = useMemo(
    () =>
      (Array.isArray(licensesPayload) ? licensesPayload : []).map((license) => ({
        ...license,
        fromName: license?.createdBy?.username || license?.issuer?.username || "-",
        toName: license?.assignedTo?.username || license?.dealer || "-",
        currency: license?.currency || "INR",
        transactionType: license?.transactionType || "Sell",
        paymentAmount: Number(license?.paymentAmount ?? license?.total ?? 0),
        documentPath: license?.documentPath || "",
      })),
    [licensesPayload]
  );
  const scopedLicenses = useMemo(() => {
    return normalizedLicenses.filter((license) => {
      const creatorId = String(license?.createdBy?._id || license?.createdBy?.id || license?.issuer?._id || license?.issuer?.id || "");
      const creatorUsername = String(license?.createdBy?.username || license?.issuer?.username || "").trim().toLowerCase();
      const creatorEmail = String(license?.createdBy?.email || license?.issuer?.email || "").trim().toLowerCase();
      const creatorUniqueId = String(license?.createdBy?.uniqueId || license?.issuer?.uniqueId || "").trim().toLowerCase();
      const assigneeId = String(license?.assignedTo?._id || license?.assignedTo?.id || license?.assignedTo || "");
      const assigneeUsername = String(license?.assignedTo?.username || license?.dealer || "").trim().toLowerCase();
      const assigneeEmail = String(license?.assignedTo?.email || "").trim().toLowerCase();
      const assigneeUniqueId = String(license?.assignedTo?.uniqueId || "").trim().toLowerCase();

      return (
        (currentUserIdentity.id && creatorId === currentUserIdentity.id) ||
        (currentUserIdentity.username && creatorUsername === currentUserIdentity.username) ||
        (currentUserIdentity.email && creatorEmail === currentUserIdentity.email) ||
        (currentUserIdentity.uniqueId && creatorUniqueId === currentUserIdentity.uniqueId) ||
        (currentUserIdentity.id && assigneeId === currentUserIdentity.id) ||
        (currentUserIdentity.username && assigneeUsername === currentUserIdentity.username) ||
        (currentUserIdentity.email && assigneeEmail === currentUserIdentity.email) ||
        (currentUserIdentity.uniqueId && assigneeUniqueId === currentUserIdentity.uniqueId)
      );
    });
  }, [currentUserIdentity, normalizedLicenses]);

  const licenseSummary = useMemo(() => {
    return scopedLicenses.reduce(
      (acc, license) => {
        const units = Math.max(0, Number(license?.count || 0));
        const isPurchase = String(license?.transactionType || "Sell").toLowerCase() !== "reverse";
        if (isPurchase && units > 0) {
          acc.purchaseRecords += 1;
          acc.totalPurchased += units;
          acc.available += Math.max(0, Number(license?.remainingCount ?? units));
        }
        const lifecycle = getLicenseLifecycle(license);
        if (lifecycle === "expired") {
          acc.expired += isPurchase ? units : 0;
        } else if (lifecycle === "expiring") {
          acc.expiring += isPurchase ? units : 0;
        } else {
          acc.active += isPurchase ? units : 0;
        }
        return acc;
      },
      { totalPurchased: 0, available: 0, purchaseRecords: 0, active: 0, expiring: 0, expired: 0 }
    );
  }, [scopedLicenses]);

  const purchasesByRecipient = useMemo(() => {
    const totals = new Map();
    scopedLicenses.forEach((license) => {
      if (String(license?.transactionType || "Sell").toLowerCase() === "reverse") return;
      const recipientId = String(license?.assignedTo?._id || license?.assignedTo?.id || license?.assignedTo || license?.toName || "");
      if (!recipientId) return;
      const current = totals.get(recipientId) || { units: 0, purchases: 0 };
      current.units += Math.max(0, Number(license?.count || 0));
      current.purchases += 1;
      totals.set(recipientId, current);
    });
    return totals;
  }, [scopedLicenses]);

  const filteredLicenses = useMemo(() => {
    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
    const query = String(searchTerm || "").trim().toLowerCase();

    return scopedLicenses.filter((license) => {
      if (selectedDealer && String(license?.assignedTo?._id || "") !== String(selectedDealer)) return false;
      if (selectedType && String(license?.type || "") !== selectedType) return false;
      if (selectedOperation && String(license?.transactionType || "") !== selectedOperation) return false;
      if (selectedStatus && String(license?.status || "") !== selectedStatus) return false;
      if (transactionToggle && String(license?.transactionType || "Sell") !== transactionToggle) return false;

      const createdAt = license?.createdAt ? new Date(license.createdAt) : null;
      if (fromDate && createdAt && createdAt < fromDate) return false;
      if (toDate && createdAt && createdAt > toDate) return false;

      if (query) {
        const searchHaystack = [
          license?.fromName,
          license?.toName,
          license?.remarks,
          license?.type,
          license?.transactionType,
          license?.status,
        ]
          .join(" ")
          .toLowerCase();
        if (!searchHaystack.includes(query)) return false;
      }

      return true;
    });
  }, [
    scopedLicenses,
    selectedDealer,
    selectedType,
    selectedOperation,
    selectedStatus,
    dateFrom,
    dateTo,
    transactionToggle,
    searchTerm,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredLicenses.length / rowsPerPage));
  const pagedLicenses = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredLicenses.slice(start, start + rowsPerPage);
  }, [filteredLicenses, page, rowsPerPage]);

  const totalUnits = useMemo(
    () => filteredLicenses.reduce((sum, license) => sum + Number(license?.count || 0), 0),
    [filteredLicenses]
  );

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const baseTotal = Number(next.unitAmount || 0) * Number(next.count || 0);
      const taxAmount = (baseTotal * Number(next.tax || 0)) / 100;
      next.total = baseTotal + taxAmount;
      if (field !== "paymentAmount") {
        next.paymentAmount = next.total;
      }
      return next;
    });
  };

  const resetForm = () => {
    setFormData(createDefaultFormData());
  };

  const handleApplyFilters = () => {
    setPage(1);
  };

  const handleResetFilters = () => {
    setSelectedDealer("");
    setSelectedType("");
    setSelectedOperation("");
    setSelectedStatus("");
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setTransactionToggle("Sell");
    setPage(1);
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredLicenses);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "licenses.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Licenses CSV downloaded.");
  };

  const handleCreateLicense = async () => {
    if (!canPurchaseLicense) {
      showStatus("error", "You are not allowed to purchase licenses.");
      return;
    }
    if (!formData.dealer) {
      showStatus("warning", "Please select a dealer.");
      return;
    }
    if (!formData.paymentDate) {
      showStatus("warning", "Please select payment date.");
      return;
    }
    if (Number(formData.count || 0) <= 0) {
      showStatus("warning", "Count must be greater than 0.");
      return;
    }

    const payload = new FormData();
    payload.append("dealer", String(formData.dealer || ""));
    payload.append("unitAmount", String(formData.unitAmount || 0));
    payload.append("currency", String(formData.currency || "INR"));
    payload.append("count", String(formData.count || 0));
    payload.append("tax", String(formData.tax || 0));
    payload.append("total", String(formData.total || 0));
    payload.append("status", String(formData.status || "Completed"));
    payload.append("paymentAmount", String(formData.paymentAmount || 0));
    payload.append("paymentDate", String(formData.paymentDate || ""));
    payload.append("type", String(formData.type || "Yearly"));
    payload.append("remarks", String(formData.remarks || ""));
    payload.append("transactionType", String(formData.transactionType || "Sell"));
    if (formData.file) {
      payload.append("file", formData.file);
    }

    try {
      await addLicense(payload).unwrap();
      await refetch();
      resetForm();
      setMode("table");
      setPurchaseModalOpen(false);
      showStatus("success", "License purchased successfully.");
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Failed to save license.");
    }
  };

  const handleReverseLicense = async (license) => {
    const reverseableCount = Math.max(0, Number(license?.remainingCount ?? license?.count ?? 0));
    if (!license?._id || reverseableCount <= 0) {
      showStatus("warning", "No reverseable license count available.");
      return;
    }

    try {
      await updateLicense({
        id: license._id,
        reverseLicense: true,
      }).unwrap();
      await refetch();
      showStatus("success", `${reverseableCount} license(s) reversed successfully.`);
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Failed to reverse license.");
    }
  };

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!asPage) return;
    if (initialSection === "purchase-license" && canPurchaseLicense) {
      setPurchaseModalOpen(true);
      return;
    }
    const sectionMap = {
      licenses: summaryRef,
      "all-licenses": tableRef,
      "license-summary": summaryRef,
      transactions: tableRef,
    };
    const target = sectionMap[initialSection]?.current;
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [asPage, canPurchaseLicense, initialSection]);

  const summaryCards = [
    {
      key: "purchased",
      label: "Total Purchased Licenses",
      value: licenseSummary.totalPurchased,
      detail: "All purchased units",
      tone: "neutral",
      icon: <ReceiptLongRoundedIcon fontSize="small" />,
    },
    {
      key: "used",
      label: "Licenses In Use",
      value: Math.max(0, licenseSummary.totalPurchased - licenseSummary.available),
      detail: "Assigned to vehicles",
      tone: "success",
      icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
    },
    {
      key: "available",
      label: "Licenses Available",
      value: licenseSummary.available,
      detail: "Ready to assign",
      tone: "warning",
      icon: <WarningAmberRoundedIcon fontSize="small" />,
    },
    {
      key: "purchases",
      label: "Purchase Records",
      value: licenseSummary.purchaseRecords,
      detail: "Number of times purchased",
      tone: "danger",
      icon: <HighlightOffRoundedIcon fontSize="small" />,
    },
  ];

  const tableSection = (
    <Paper variant="outlined" sx={{ ...panelSx, p: { xs: 2, md: 2.5 } }} ref={tableRef}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>All Licenses</Typography>
            <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 14 }}>
              Filter and review purchased licenses without changing the existing data flow.
            </Typography>
          </Box>

          {asPage && canPurchaseLicense ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPurchaseModalOpen(true)}
              sx={{
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 800,
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                boxShadow: "0 16px 32px rgba(37,99,235,0.22)",
              }}
            >
              Purchase License
            </Button>
          ) : null}
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by dealer or remarks..."
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListRoundedIcon />}
              onClick={handleApplyFilters}
              sx={{ minHeight: 40, borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
            >
              Filters
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedDealer}
              onChange={(e) => setSelectedDealer(e.target.value)}
              sx={inputSx}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value) return "All Dealers";
                  const matched = normalizedRecipients.find((item) => String(item._id) === String(value));
                  return matched?.username || value;
                },
              }}
            >
              <MenuItem value="">All Dealers</MenuItem>
              {normalizedRecipients.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.username}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              sx={inputSx}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => value || "All License Types",
              }}
            >
              <MenuItem value="">All License Types</MenuItem>
              {LICENSE_TYPES.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedOperation}
              onChange={(e) => setSelectedOperation(e.target.value)}
              sx={inputSx}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => value || "All Operations",
              }}
            >
              <MenuItem value="">All Operations</MenuItem>
              {TRANSACTION_TYPES.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={inputSx}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => value || "All Statuses",
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {LICENSE_STATUSES.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", lg: "center" }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 700, color: transactionToggle === "Sell" ? "#0f172a" : "#94a3b8" }}>Sell</Typography>
                <Switch checked={transactionToggle === "Reverse"} onChange={(e) => setTransactionToggle(e.target.checked ? "Reverse" : "Sell")} />
                <Typography sx={{ fontWeight: 700, color: transactionToggle === "Reverse" ? "#0f172a" : "#94a3b8" }}>Reverse</Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button variant="outlined" onClick={handleResetFilters} sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}>
                  Reset
                </Button>
                <IconButton onClick={() => refetch()} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5 }}>
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={handleDownloadCsv} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5 }}>
                  <DownloadIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <TableContainer sx={{ borderRadius: 3, border: "1px solid #eef2f7", maxHeight: asPage ? "68vh" : "56vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {["From", "To", "License Type", "Operation", "Amount", "This Purchase", "Total Purchased", "Purchase Count", "Status", "Payment", "Payment Date", "Type", "Action"].map((head) => (
                  <TableCell key={head} sx={{ fontWeight: 800, color: "#475569", bgcolor: "#f8fafc", whiteSpace: "nowrap" }}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedLicenses.length ? (
                pagedLicenses.map((license) => {
                  const tone = statusTone(getLicenseLifecycle(license));
                  const recipientId = String(license?.assignedTo?._id || license?.assignedTo?.id || license?.assignedTo || license?.toName || "");
                  const purchaseTotals = purchasesByRecipient.get(recipientId) || { units: 0, purchases: 0 };
                  return (
                    <TableRow key={license._id} hover>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{license.fromName}</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{license.toName}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{license.type}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{license.transactionType}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{license.currency}{Number(license.unitAmount || 0)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{Number(license.count || 0)}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{purchaseTotals.units}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{purchaseTotals.purchases}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={tone.label}
                          sx={{ bgcolor: tone.bg, color: tone.color, fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{license.currency}{Number(license.paymentAmount || 0)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{formatDateCell(license.paymentDate)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{license.currency}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {license.documentPath ? (
                            <IconButton
                              component="a"
                              href={license.documentPath}
                              target="_blank"
                              rel="noreferrer"
                              sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}
                            >
                              <DescriptionOutlinedIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>-</Typography>
                          )}
                          {String(license.transactionType || "Sell") === "Sell" && Number(license.remainingCount ?? license.count ?? 0) > 0 ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={isUpdatingLicense}
                              onClick={() => handleReverseLicense(license)}
                              sx={{
                                minWidth: 82,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 800,
                              }}
                            >
                              Reverse
                            </Button>
                          ) : null}
                          <Typography sx={{ color: "#64748b", fontSize: 12.5, maxWidth: 110 }} noWrap>
                            {license.remarks || "No remarks"}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 7, color: "#64748b" }}>
                    No licenses found.
                  </TableCell>
                </TableRow>
              )}

              {pagedLicenses.length ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: "center", fontWeight: 900, bgcolor: "#fcfcfd" }}>
                    Total Purchased Licenses: {totalUnits}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
        >
          <Typography sx={{ color: "#475569", fontSize: 14 }}>
            Showing {filteredLicenses.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredLicenses.length)} of {filteredLicenses.length} entries
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ color: "#475569", fontSize: 14 }}>Rows per page:</Typography>
              <FormControl size="small" sx={{ minWidth: 86 }}>
                <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Pagination page={page} count={Math.max(1, totalPages)} onChange={(_, value) => setPage(value)} shape="rounded" />
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );

  const purchaseFormSection = (
    <Paper
      variant="outlined"
      sx={{
        ...panelSx,
        p: { xs: 2, md: 2.5 },
        position: asPage ? { lg: "sticky" } : "relative",
        top: asPage ? { lg: 24 } : "auto",
      }}
      ref={formRef}
    >
      <Stack spacing={2.2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
          <Box>
            <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>Purchase License</Typography>
            <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 14 }}>
              Assign licenses to an eligible Sub Admin or Dealer account.
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              background: "linear-gradient(135deg, rgba(37,99,235,0.12), rgba(34,197,94,0.12))",
              color: "#2563eb",
            }}
          >
            <AddCardRoundedIcon />
          </Box>
        </Stack>

        <Stack spacing={1.5}>
          <Box sx={purchaseSectionCardSx}>
            <Typography sx={purchaseSectionTitleSx}>Recipient & Plan</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={7} lg={7}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Recipient *"
                  value={formData.dealer}
                  onChange={(e) => handleFormChange("dealer", e.target.value)}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          minWidth: 420,
                          width: "max-content",
                          maxWidth: 640,
                          maxHeight: 360,
                        },
                      },
                    },
                  }}
                  sx={{
                    ...inputSx,
                    minWidth: { md: 380, lg: 420 },
                    "& .MuiOutlinedInput-root": {
                      ...inputSx["& .MuiOutlinedInput-root"],
                      minWidth: { md: 380, lg: 420 },
                    },
                  }}
                >
                  <MenuItem value="">Select recipient</MenuItem>
                  {normalizedRecipients.map((item) => (
                    <MenuItem key={item._id} value={item._id}>
                      {item.username} ({String(item.role || "group_admin").replace(/_/g, " ")})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="License Type *"
                  select
                  value={formData.type}
                  onChange={(e) => handleFormChange("type", e.target.value)}
                  sx={inputSx}
                >
                  {LICENSE_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Transaction Type *"
                  select
                  value={formData.transactionType}
                  onChange={(e) => handleFormChange("transactionType", e.target.value)}
                  sx={inputSx}
                >
                  {TRANSACTION_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Box sx={purchaseSectionCardSx}>
            <Typography sx={purchaseSectionTitleSx}>Pricing</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField fullWidth size="small" label="Single Unit Amount *" type="number" value={formData.unitAmount} onChange={(e) => handleFormChange("unitAmount", Number(e.target.value))} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField fullWidth size="small" label="Count (Units) *" type="number" value={formData.count} onChange={(e) => handleFormChange("count", Number(e.target.value))} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <TextField fullWidth size="small" label="Currency *" value={formData.currency} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <TextField fullWidth size="small" label="Tax (%)" type="number" value={formData.tax} onChange={(e) => handleFormChange("tax", Number(e.target.value))} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <TextField fullWidth size="small" label="Status *" select value={formData.status} onChange={(e) => handleFormChange("status", e.target.value)} sx={inputSx}>
                  {LICENSE_STATUSES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField fullWidth size="small" label="Total Amount" type="number" value={formData.total} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField fullWidth size="small" label="Payment Amount" type="number" value={formData.paymentAmount} onChange={(e) => handleFormChange("paymentAmount", Number(e.target.value))} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField fullWidth size="small" type="date" label="Payment Date *" value={formData.paymentDate} onChange={(e) => handleFormChange("paymentDate", e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="file"
                  label="Document"
                  onChange={(e) => handleFormChange("file", e.target.files?.[0] || null)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx" }}
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={purchaseSectionCardSx}>
            <Typography sx={purchaseSectionTitleSx}>Notes</Typography>
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              multiline
              minRows={4}
              value={formData.remarks}
              onChange={(e) => handleFormChange("remarks", e.target.value)}
              placeholder="Enter remarks (optional)"
              sx={inputSx}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleCreateLicense}
            disabled={isSaving}
            sx={{
              minHeight: 46,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 800,
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
              boxShadow: "0 16px 32px rgba(37,99,235,0.22)",
            }}
          >
            Save License
          </Button>
          <Button fullWidth variant="outlined" onClick={resetForm} sx={{ minHeight: 46, borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  if (asPage) {
    return (
      <Stack spacing={2.2}>
        <Box ref={summaryRef}>
          <FlexibleStatsRow items={summaryCards} />
        </Box>

        <Grid container spacing={2.2} alignItems="flex-start">
          <Grid item xs={12}>
            {tableSection}
          </Grid>
        </Grid>

        <Dialog
          open={purchaseModalOpen}
          onClose={() => {
            setPurchaseModalOpen(false);
            resetForm();
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <DialogContent sx={{ p: 2.5, bgcolor: "#fff" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
                Purchase License
              </Typography>
              <IconButton
                onClick={() => {
                  setPurchaseModalOpen(false);
                  resetForm();
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            {purchaseFormSection}
          </DialogContent>
        </Dialog>
      </Stack>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{mode === "table" ? "Licenses" : "Purchase license"}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {mode === "table" ? (
          <Box sx={{ p: 2.5 }}>
            {tableSection}
          </Box>
        ) : (
          <>
            <Box sx={{ p: 2.5 }}>
              {purchaseFormSection}
            </Box>
            <DialogActions sx={{ px: 3, pb: 3, justifyContent: "flex-start", gap: 1.5 }}>
              <Button variant="contained" onClick={handleCreateLicense} disabled={isSaving}>Save</Button>
              <Button variant="outlined" onClick={() => setMode("table")}>Cancel</Button>
            </DialogActions>
          </>
        )}

        {mode === "table" ? (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setMode("create")}>
                Purchase License
              </Button>
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default LicensePortalModal;
